/**
 * Pure label helpers for the report workbench. They take the caller's
 * translator (react-i18next's `t`) and answer in English without one, so they
 * stay testable without an i18n instance.
 */
import { englishT, type Translate } from '../agents/agent-helpers';

/** `created_at` in the app language's date and time format; '' when unusable. */
export function formatDateTime(
  value: string | null | undefined,
  language?: string
): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(language || undefined);
  } catch {
    // An unknown language tag: fall back to the browser's own format.
    return date.toLocaleString();
  }
}

/** The text of one entry in the version dropdown ("v3 (current) — AI draft"). */
export function versionOptionLabel(
  version: { version_number: number; edited_by: unknown },
  isCurrent: boolean,
  t: Translate = englishT
): string {
  const number = version.version_number;
  const aiDraft = version.edited_by === null;
  if (isCurrent && aiDraft) {
    return t(
      'ReportWorkbenchVersionOptionCurrentAiDraft',
      'v{{number}} (current) — AI draft',
      { number }
    );
  }
  if (isCurrent) {
    return t('ReportWorkbenchVersionOptionCurrent', 'v{{number}} (current)', {
      number,
    });
  }
  if (aiDraft) {
    return t('ReportWorkbenchVersionOptionAiDraft', 'v{{number}} — AI draft', {
      number,
    });
  }
  return t('ReportWorkbenchVersionOption', 'v{{number}}', { number });
}

/** The heading of a row in the version journal ("v3 · edited"). */
export function versionRowLabel(
  version: { version_number: number; edited_by: unknown },
  t: Translate = englishT
): string {
  const number = version.version_number;
  return version.edited_by === null
    ? t('ReportWorkbenchVersionRowAiDraft', 'v{{number}} · AI draft', {
        number,
      })
    : t('ReportWorkbenchVersionRowEdited', 'v{{number}} · edited', { number });
}

/** The summary stored with a restored version. Saved in English, shown translated. */
export const restoredSummary = (versionNumber: number): string =>
  `Restored version ${versionNumber}`;

/** The summary stored with a raw-HTML save that has none of its own. */
export const RAW_EDIT_SUMMARY = 'Raw HTML edit (advanced)';

/**
 * A version's edit summary for display. The two summaries the workbench writes
 * itself are stored in English and translated here; anything a person typed is
 * shown as written.
 */
export function editSummaryLabel(
  summary: string | null | undefined,
  t: Translate = englishT
): string {
  if (!summary) return t('ReportWorkbenchNoSummary', 'No summary');
  if (summary === RAW_EDIT_SUMMARY) {
    return t('ReportWorkbenchSummaryRawEdit', RAW_EDIT_SUMMARY);
  }
  const restored = /^Restored version (\d+)$/.exec(summary);
  if (restored) {
    return t('ReportWorkbenchSummaryRestored', 'Restored version {{number}}', {
      number: restored[1],
    });
  }
  return summary;
}

/** The second line of a journal row: the summary and, when known, its date. */
export function versionSubLabel(
  version: { edit_summary?: string | null; created_at?: string | null },
  language: string | undefined,
  t: Translate = englishT
): string {
  const summary = editSummaryLabel(version.edit_summary, t);
  const date = formatDateTime(version.created_at, language);
  return date
    ? t('ReportWorkbenchVersionSub', '{{summary}} · {{date}}', {
        summary,
        date,
      })
    : summary;
}

const words = (value: string): string => String(value ?? '').replace(/_/g, ' ');

/**
 * An activity entry's action ("version added"). An action this page does not
 * know yet is shown as the API names it, underscores as spaces.
 */
export function auditActionLabel(
  action: string,
  t: Translate = englishT
): string {
  return t(`ReportWorkbenchAuditAction_${action}`, words(action));
}

/** Who did it ("employee", "client", "system", "ai"); unknown types as sent. */
export function auditActorLabel(
  actorType: string,
  t: Translate = englishT
): string {
  return t(`ReportWorkbenchActor_${actorType}`, String(actorType ?? ''));
}

/** The workbench calls that can fail, each with its own fallback text. */
export type WorkbenchAction =
  | 'load'
  | 'content'
  | 'save'
  | 'rawSave'
  | 'restore';

const ACTION_FALLBACK: Record<WorkbenchAction, [string, string]> = {
  load: ['ReportWorkbenchLoadFailed', 'Failed to load the report.'],
  content: [
    'ReportWorkbenchContentLoadFailed',
    'Failed to load the report content.',
  ],
  save: ['ReportWorkbenchSaveFailed', 'Failed to save the report.'],
  rawSave: ['ReportWorkbenchRawSaveFailed', 'Failed to save raw HTML.'],
  restore: ['ReportWorkbenchRestoreFailed', 'Failed to restore that version.'],
};

/** The error codes the report endpoints answer with, each with its own text. */
const ERROR_CODE_TEXT: Record<string, [string, string]> = {
  invalid_token: [
    'ReportWorkbenchErrorSession',
    'Your session has expired. Sign in again to continue.',
  ],
  forbidden: [
    'ReportWorkbenchErrorForbidden',
    'You do not have access to this report.',
  ],
  report_not_found: [
    'ReportWorkbenchErrorReportNotFound',
    'This report no longer exists.',
  ],
  version_not_found: [
    'ReportWorkbenchErrorVersionNotFound',
    'That version of the report no longer exists.',
  ],
  report_html_unavailable: [
    'ReportWorkbenchErrorHtmlUnavailable',
    'The content of this version is not available.',
  ],
  report_too_large: [
    'ReportWorkbenchTooLarge',
    'The report is too large to save.',
  ],
  invalid_state: [
    'ReportWorkbenchNotEditable',
    'This report can no longer be edited in its current state.',
  ],
};

const SAVING_ACTIONS: WorkbenchAction[] = ['save', 'rawSave', 'restore'];

/**
 * The text for a failed workbench call, chosen by the error's code and never
 * taken from the server's own message. A code this page does not know, or
 * none at all (a validation error, a server failure, no connection), gives the
 * action's own fallback; a conflict while saving always means the report is no
 * longer editable.
 */
export function workbenchErrorMessage(
  action: WorkbenchAction,
  error: { status?: number; code?: string } | null | undefined,
  t: Translate = englishT
): string {
  const code = typeof error?.code === 'string' ? error.code : '';
  const known = Object.prototype.hasOwnProperty.call(ERROR_CODE_TEXT, code)
    ? ERROR_CODE_TEXT[code]
    : error?.status === 409 && SAVING_ACTIONS.includes(action)
    ? ERROR_CODE_TEXT.invalid_state
    : ACTION_FALLBACK[action];
  return t(known[0], known[1]);
}
